/**
 * Select Component — HMVMReviewer Design System Phase 1
 * 
 * Features:
 * - Native select element with custom styling
 * - Label and error message support
 * - Full accessibility (ARIA, required indicator)
 * - Uses semantic theme tokens
 * - Square geometry (border-radius: 0)
 */

import { SelectHTMLAttributes } from 'react';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  helperText?: string;
  options: SelectOption[];
  placeholder?: string;
  fullWidth?: boolean;
}

export function Select({
  label,
  error,
  helperText,
  options,
  placeholder,
  fullWidth = false,
  className = '',
  id,
  required,
  disabled,
  ...props
}: SelectProps) {
  const selectId = id || `select-${Math.random().toString(36).slice(2, 11)}`;
  const errorId = error ? `${selectId}-error` : undefined;
  const helperId = helperText ? `${selectId}-helper` : undefined;

  return (
    <div className={`flex flex-col gap-1.5 ${fullWidth ? 'w-full' : ''}`}>
      {label && (
        <label
          htmlFor={selectId}
          className="text-[var(--reviewer-text)] text-[var(--reviewer-text-sm)] font-medium font-mono"
        >
          {label}
          {required && (
            <span className="text-[var(--reviewer-danger)] ml-1" aria-label="required">
              *
            </span>
          )}
        </label>
      )}
      
      <select
        id={selectId}
        disabled={disabled}
        required={required}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={[errorId, helperId].filter(Boolean).join(' ') || undefined}
        className={`
          w-full px-3 py-2
          bg-[var(--reviewer-input-bg)]
          border border-[var(--reviewer-input-border)]
          text-[var(--reviewer-text)]
          font-mono text-[var(--reviewer-text-base)]
          transition-all duration-150
          focus:outline-none
          focus:border-[var(--reviewer-input-focus-border)]
          focus:ring-2 focus:ring-[var(--reviewer-input-focus-ring)]
          disabled:opacity-50 disabled:cursor-not-allowed
          ${error ? 'border-[var(--reviewer-danger-border)] focus:border-[var(--reviewer-danger)] focus:ring-[var(--reviewer-danger-soft)]' : ''}
          ${className}
        `}
        {...props}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </option>
        ))}
      </select>
      
      {error && (
        <p
          id={errorId}
          className="text-[var(--reviewer-danger)] text-[var(--reviewer-text-xs)] font-mono"
          role="alert"
        >
          {error}
        </p>
      )}
      
      {helperText && !error && (
        <p
          id={helperId}
          className="text-[var(--reviewer-text-secondary)] text-[var(--reviewer-text-xs)] font-mono"
        >
          {helperText}
        </p>
      )}
    </div>
  );
}
